import { Component } from '@theme/component';

/**
 * A custom element that types out a rotating list of words.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wordEl - The element that displays the typed word.
 *
 * @extends Component<Refs>
 */
class TypewriterText extends Component {
  requiredRefs = ['wordEl'];

  connectedCallback() {
    super.connectedCallback();

    /** @type {string[]} */
    this.words = [];

    try {
      this.words = JSON.parse(this.getAttribute('data-words') || '[]').filter(Boolean);
    } catch {
      this.words = [];
    }

    if (this.words.length === 0) return;

    this.typingSpeed = Number(this.getAttribute('data-typing-speed')) || 80;
    this.pauseDuration = Number(this.getAttribute('data-pause-duration')) || 1400;
    this.deletingSpeed = Math.max(30, this.typingSpeed / 2);
    this.wordIndex = 0;
    this.charIndex = 0;
    this.isDeleting = false;

    if (this.words.length === 1) {
      this.refs.wordEl.textContent = this.words[0] ?? '';
      return;
    }

    this.#tick();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.timeoutId);
  }

  #tick = () => {
    const currentWord = this.words[this.wordIndex];
    if (!currentWord) return;

    this.charIndex += this.isDeleting ? -1 : 1;
    this.refs.wordEl.textContent = currentWord.slice(0, this.charIndex);

    let delay = this.isDeleting ? this.deletingSpeed : this.typingSpeed;

    if (!this.isDeleting && this.charIndex === currentWord.length) {
      delay = this.pauseDuration;
      this.isDeleting = true;
    } else if (this.isDeleting && this.charIndex === 0) {
      this.isDeleting = false;
      this.wordIndex = (this.wordIndex + 1) % this.words.length;
      delay = this.typingSpeed;
    }

    this.timeoutId = setTimeout(this.#tick, delay);
  };
}

if (!customElements.get('typewriter-text')) {
  customElements.define('typewriter-text', TypewriterText);
}
